/**
 * Post-Processing Script. to apply one or more graphical effects like Depth-Of-Field, Bloom, Film Grain or various types of Anti-aliasing
 */

import {
	Mesh,
	DoubleSide,
	NearestFilter,
	LinearFilter,
	RGBAFormat,
	OrthographicCamera,
	BufferGeometry,
	BufferAttribute,
	ShaderMaterial,
	WebGLRenderTarget,
	Vector2,
    DepthTexture,
    DepthFormat,
    DepthStencilFormat,
    UnsignedInt248Type,
    RawShaderMaterial,
	FloatType,

    AnimationMixer,
    HalfFloatType,
    MathUtils,
} from 'three'

import { settingsData, threeJsVariables } from '../../settings';
import { ViewMode } from '../../common';
import { AnimationObject } from '../liveAnimations/AnimateObject';

let renderer = null
let camera   = null
let canvas   = null
let scene    = null
let dirLight = null

let WIDTH    = 64
let HEIGHT   = 64
let aspect   = 1.0

let rt1 = null
let rt2 = null
let rt3 = null

let postprocessing_active = false

let bokeh_active   = false
let bokeh_focus    = 80
let bokeh_aperture = 0.5/10000.0
let bokeh_maxblur  = 0.005

let trans_active   = false
let trans_scale    = 0.0
let trans_opacity  = 0.0

let lut_active       = false

let mixers           = []

let renderPasses     = []

const _vector2 = new Vector2()

const orto_camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1)

const pass_geometry = new BufferGeometry()
pass_geometry.setAttribute('position', new BufferAttribute(new Float32Array([-1, -1, 0, -1, 1, 0, 1, -1, 0, 1, -1, 0, -1, 1, 0, 1, 1, 0]), 3))
pass_geometry.setAttribute('uv', new BufferAttribute(new Float32Array([0, 0, 0, 1, 1, 0, 1, 0, 0, 1, 1, 1]), 2))

const screen_copy = new Mesh(pass_geometry, new ShaderMaterial({
    uniforms: {
        map     : { value: null },
    },
    vertexShader: [
        'varying vec2 vUv;',
        'void main() {',
            'vUv = uv;',
            'gl_Position = vec4(position,1.0);',
        '}'].join(''),
    fragmentShader: [
        'uniform sampler2D map;',
        'varying vec2 vUv;',
        //'const vec4 UnpackFactors = 1.0 / vec4(  256. * 256. * 256., 256. * 256., 256., 1. );',
        //'float unpackRGBAToDepth( const in vec4 v ) { return dot( v, UnpackFactors ); }',
        'void main(){',
            //'float depth = unpackRGBAToDepth(texture2D( map, vUv));',
			//'gl_FragColor = vec4(depth,depth,depth,1.0);',
			'gl_FragColor = texture2D( map, vUv);',
        '}'].join(''),
        side        : DoubleSide,
        depthTest   : false,
        depthWrite  : false,
        transparent : false,
}))

/**
 * Depth-of-field shader with bokeh
 * ported from GLSL shader by Martins Upitis
 * http://artmartinsh.blogspot.com/2010/02/glsl-lens-blur-filter-with-bokeh.html
 */
const Bokeh = new Mesh(pass_geometry,new ShaderMaterial({
    uniforms: {
        tColor  : { value: null   },
        tDepth  : { value: null   },
        focus   : { value: 1.0    },
        aspect  : { value: 1.0    },
        aperture: { value: 0.025  },
        maxblur : { value: 0.01   },
        far     : { value: 4000   },
    },
    vertexShader:[
        'varying vec2 vUv;',
        'void main() {',
            'vUv = uv;',
            'gl_Position = vec4( position, 1.0 );',
        '}'].join(''),
    fragmentShader:`
        varying vec2 vUv;
        uniform sampler2D tColor;
        uniform sampler2D tDepth;
        uniform float maxblur; // max blur amount
        uniform float aperture; // aperture - bigger values for shallower depth of field
        uniform float focus;
        uniform float aspect;
        uniform float far;
        float perspectiveDepthToViewZ( const in float invClipZ, const in float near, const in float far ) {
            return ( near * far ) / ( ( far - near ) * invClipZ - far );
        }
        void main() {
            vec2 aspectcorrect = vec2( 1.0, aspect );
            float viewZ = perspectiveDepthToViewZ(texture2D( tDepth, vUv).x, 1.0, far );
            float factor = ( focus + viewZ ); // viewZ is <= 0, so this is a difference equation
            vec2 dofblur = vec2 ( clamp( factor * aperture, -maxblur, maxblur ) );
            vec2 dofblur9 = dofblur * 0.9;
            vec2 dofblur7 = dofblur * 0.7;
            vec2 dofblur4 = dofblur * 0.4;
            vec4 col = vec4( 0.0 );
            col += texture2D( tColor, vUv.xy );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,   0.4  ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.15,  0.37 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.29,  0.29 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.37,  0.15 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.40,  0.0  ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.37, -0.15 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.29, -0.29 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.15, -0.37 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,  -0.4  ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.15,  0.37 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29,  0.29 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.37,  0.15 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.4,   0.0  ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.37, -0.15 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29, -0.29 ) * aspectcorrect ) * dofblur );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.15, -0.37 ) * aspectcorrect ) * dofblur );

            col += texture2D( tColor, vUv.xy + ( vec2(  0.15,  0.37 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.37,  0.15 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.37, -0.15 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.15, -0.37 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.15,  0.37 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.37,  0.15 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.37, -0.15 ) * aspectcorrect ) * dofblur9 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.15, -0.37 ) * aspectcorrect ) * dofblur9 );

            col += texture2D( tColor, vUv.xy + ( vec2(  0.29,  0.29 ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.40,  0.0  ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.29, -0.29 ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,  -0.4  ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29,  0.29 ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.4,   0.0  ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29, -0.29 ) * aspectcorrect ) * dofblur7 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,   0.4  ) * aspectcorrect ) * dofblur7 );

            col += texture2D( tColor, vUv.xy + ( vec2(  0.29,  0.29 ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.4,   0.0  ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.29, -0.29 ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,  -0.4  ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29,  0.29 ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.4,   0.0  ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2( -0.29, -0.29 ) * aspectcorrect ) * dofblur4 );
            col += texture2D( tColor, vUv.xy + ( vec2(  0.0,   0.4  ) * aspectcorrect ) * dofblur4 );

            gl_FragColor = col / 41.0;
            gl_FragColor.a = 1.0;
        }`,
    side        : DoubleSide,
    depthTest   : false,
    depthWrite  : false,
    transparent : false,
}))

const layer_mesh_opacity2  = new Mesh(pass_geometry, new RawShaderMaterial({
    uniforms: {
        map     : { value: null },
        scale   : { value: 1.0  },
        opacity : { value: 0.0  },
    },
    vertexShader: [
        'precision highp float;',
        'precision highp int;',
        'attribute vec3 position;',
        'attribute vec2 uv;',
        'varying vec2 lUv;',
        'varying vec2 lp;',
        'void main() {',
            'lUv = uv;',
            'lp.x = position.x;',
            'lp.y = position.y;',
            'gl_Position = vec4(position,1.0);',
        '}'].join(''),
    fragmentShader: [
        'precision highp float;',
        'precision highp int;',
        'uniform sampler2D map;',
        'uniform float opacity;',
        'uniform float scale;',
        'varying vec2 lUv;',
        'varying vec2 lp;',
        'void main(){',
            'vec4 cmap = texture2D(map, lUv);',
            'vec3 color = vec3(0.0);',
            'float total = 0.0;',
            'vec2 toCenter = vec2(0.5, 0.5) - lUv;',
            'for (float t = 0.0; t <= 50.0; t++) {',
                'float percent = t / 50.0;',
                'float weight = 4.0 * (percent - percent * percent);',
                'color += texture2D(map, lUv + toCenter * percent* (0.5-opacity*0.5)).rgb*weight;',
                'total += weight;',
            '}',
            'gl_FragColor = vec4(mix(color/total,vec3(1.0,1.0,1.0),scale), 1.0 );',
        '}'].join(''),
    side       : DoubleSide,
    depthTest  : false,
    depthWrite : false,
    transparent: true,
}))

const lutPass = new Mesh(pass_geometry,new ShaderMaterial({
    defines: {
		USE_3DTEXTURE: 1,
	},
	uniforms: {
		tDiffuse   : { value: null },
        intensity  : { value: 0.0  },
		lut        : { value: null },
		lut3d      : { value: null },
		lutSize    : { value: 0.0  },
	},
	vertexShader: `
		varying vec2 vUv;
		void main() {
			vUv = uv;
            gl_Position = vec4(position,1.0);
		}`,
	fragmentShader: `
		uniform float lutSize;
		#if USE_3DTEXTURE
		precision highp sampler3D;
		uniform sampler3D lut3d;
		#else
		uniform sampler2D lut;

		vec3 lutLookup( sampler2D tex, float size, vec3 rgb ) {

			float sliceHeight = 1.0 / size;
			float yPixelHeight = 1.0 / ( size * size );

			// Get the slices on either side of the sample
			float slice = rgb.b * size;
			float interp = fract( slice );
			float slice0 = slice - interp;
			float centeredInterp = interp - 0.5;

			float slice1 = slice0 + sign( centeredInterp );

			// Pull y sample in by half a pixel in each direction to avoid color
			// bleeding from adjacent slices.
			float greenOffset = clamp( rgb.g * sliceHeight, yPixelHeight * 0.5, sliceHeight - yPixelHeight * 0.5 );

			vec2 uv0 = vec2(
				rgb.r,
				slice0 * sliceHeight + greenOffset
			);
			vec2 uv1 = vec2(
				rgb.r,
				slice1 * sliceHeight + greenOffset
			);

			vec3 sample0 = texture2D( tex, uv0 ).rgb;
			vec3 sample1 = texture2D( tex, uv1 ).rgb;

			return mix( sample0, sample1, abs( centeredInterp ) );

		}
		#endif

		varying vec2 vUv;
		uniform float intensity;
		uniform sampler2D tDiffuse;
		void main() {

			vec4 val = texture2D( tDiffuse, vUv );
			vec4 lutVal;

			// pull the sample in by half a pixel so the sample begins
			// at the center of the edge pixels.
			float pixelWidth = 1.0 / lutSize;
			float halfPixelWidth = 0.5 / lutSize;
			vec3 uvw = vec3( halfPixelWidth ) + val.rgb * ( 1.0 - pixelWidth );

			#if USE_3DTEXTURE
			lutVal = vec4( texture( lut3d, uvw ).rgb, val.a );
			#else
			lutVal = vec4( lutLookup( lut, lutSize, uvw ), val.a );
			#endif
			gl_FragColor = vec4( mix( val, lutVal, intensity ) );
		}
	`,
    side       : DoubleSide,
    depthTest  : false,
    depthWrite : false,
    transparent: false,
}))

export const postProcessingResize = (width,height)=>{
  threeJsVariables.composer.setSize(width,height);
  return;
    if (WIDTH===width && HEIGHT===height){
        return
    }

    //const dpr = renderer.getPixelRatio()

    WIDTH   = width
    HEIGHT  = height
    aspect  = WIDTH/HEIGHT

    //camera.aspect = aspect
    //camera.updateProjectionMatrix()

    //renderer.setSize( WIDTH, HEIGHT )

    rt1.setSize(WIDTH, HEIGHT)
    rt2.setSize(WIDTH, HEIGHT)
    rt3.setSize(WIDTH, HEIGHT)

    /*
    // --------- bloom
    let resx = Math.round( WIDTH / 2 )
    let resy = Math.round( HEIGHT / 2 )

    bloom_rt.setSize( resx, resy )
    for ( let i=0; i<bloom_nmips; i++ ) {
        bloom_rt_h[i].setSize( resx, resy )
        bloom_rt_v[i].setSize( resx, resy )

        bloom_blur_m[i].material.uniforms.texSize.value.set(resx, resy)

        resx = Math.round( resx/2 )
        resy = Math.round( resy/2 )
    }
    */

}

export const createRenderTarget = (width,height)=>{
    const rt = new WebGLRenderTarget(
        width, height,
        {
            minFilter     : LinearFilter,
            magFilter     : LinearFilter,
            format        : RGBAFormat,
            depthBuffer   : true,
            stencilBuffer : false
        }
    )

    rt.texture.generateMipmaps = false
    rt.depthTexture        = new DepthTexture()
    rt.depthTexture.format = DepthStencilFormat
    rt.depthTexture.type   = UnsignedInt248Type
    return rt
}

/**
 * Init Post-Processing and add it to render
 */

const createRenderTargets = ()=>{

	if (renderer.capabilities.isWebGL2){
		rt1 = new WebGLRenderTarget( //new WebGLRenderTarget(
			WIDTH, HEIGHT,
			{
				minFilter     : LinearFilter,
				magFilter     : NearestFilter,
				format        : RGBAFormat,
				depthBuffer   : true,
				stencilBuffer : false,
			}
		)

        let samples = renderer.capabilities.maxSamples
        if (window.devicePixelRatio>=2){
            samples = Math.trunc(samples / 2)
        }
        rt1.samples = samples

		rt1.texture.generateMipmaps = false
		rt1.depthTexture        = new DepthTexture()
        rt1.depthTexture.format = DepthFormat //DepthFormat
	    rt1.depthTexture.type   = FloatType //UnsignedIntType //FloatType

    }else{

		rt1 = new WebGLRenderTarget(
			WIDTH, HEIGHT,
			{
				minFilter     : LinearFilter,
				magFilter     : NearestFilter,
				format        : RGBAFormat,
				depthBuffer   : true,
				stencilBuffer : false
			}
		)

		rt1.texture.generateMipmaps = false
		// настройки для webgl1
		rt1.depthTexture        = new DepthTexture()
    	rt1.depthTexture.format = DepthFormat //DepthStencilFormat
	    rt1.depthTexture.type   = FloatType //UnsignedShortType //UnsignedInt248Type
    }


    //--------------------------------------------
    rt2 = new WebGLRenderTarget(
        WIDTH, HEIGHT,
        {
            minFilter     : NearestFilter,
            magFilter     : NearestFilter,
            format        : RGBAFormat,
            depthBuffer   : false,
            stencilBuffer : false
        }
    )
    //--------------------------------------------
    rt3 = new WebGLRenderTarget(
        WIDTH, HEIGHT,
        {
            minFilter     : NearestFilter,
            magFilter     : NearestFilter,
            format        : RGBAFormat,
            depthBuffer   : false,
            stencilBuffer : false
        }
    )
}

export const setAnimations = (obj,animations,instanceId=null)=>{
    let mixer = new AnimationObject(obj,instanceId)
    let action = mixer.clipAction( animations[0] )
    action.play()
    mixers.push(mixer)
}

export const isPostProcessingActive = ()=>postprocessing_active

// export const postProcessing = (_renderer,_camera,_canvas,_scene,_dirlight = null) => {
//   return;
//     renderer = _renderer
//     camera   = _camera
//     canvas   = _canvas
//     scene    = _scene
// 	dirLight = _dirlight
//
//     WIDTH   = canvas.width
//     HEIGHT  = canvas.height
//     aspect  = WIDTH/HEIGHT
//
//     postprocessing_active = true
//
//     createRenderTargets()
//
//     //DOFMipMap.material.uniforms.tColor.value = rt1.texture
//     //DOFMipMap.material.uniforms.tDepth.value = rt1.depthTexture
//
//     Bokeh.material.uniforms.tColor.value = rt1.texture
//     Bokeh.material.uniforms.tDepth.value = rt1.depthTexture
//     Bokeh.material.uniforms.far.value    = camera.far
//
//     //prepareBloom()
//
//     //renderResize()
//
//     /*
//     const {renderer, scene, perspectiveCamera: camera} = threeJsVariables;
//     const {depthOfFields: dof, depthOfFields2: dof2, ambientOcclusion: ao, effectFXAA} = settingsData.postProcessing;
//
//     if (dof || ao || effectFXAA) {
//         const composer = new EffectComposer( renderer );
//         threeJsVariables.composer = composer;
//
//         const renderPass = new RenderPass( scene, camera );
//         composer.addPass( renderPass );
//
//         ao && ambientOcclusion(composer, scene, camera);
//         dof && depthOfFields(composer, scene, camera);
//         effectFXAA && highlightsElements(composer, scene, camera);
//     }
//
//     dof2 && depthOfFields2(scene, camera)
//     */
// }

// export const setTransEffect = (active,scale,opacity)=>{
//     trans_active = active
//     trans_scale = scale
//     trans_opacity = opacity
// }


// export const renderSceneToRT = (renderer,scene,camera)=>{
//     renderer.autoClear = false
//     renderer.getSize(_vector2)
//     //const rt = createRenderTarget(_u.x*0.25,_u.y*0.25)
//     rt2 = createRenderTarget(_vector2.x,_vector2.y)
//
//     renderer.setRenderTarget(rt2)
//     renderer.render(scene,camera)
// }

// export const renderTransition = (renderer)=>{
//     renderer.setRenderTarget( null )
//     layer_mesh_opacity2.material.uniforms.map.value     = rt2.texture
//     layer_mesh_opacity2.material.uniforms.opacity.value = trans_opacity
//     layer_mesh_opacity2.material.uniforms.scale.value   = trans_scale
//     renderer.render(layer_mesh_opacity2, orto_camera)
//
//     rt2.dispose()
//     renderer.autoClear = true
// }

// const renderTransition2 = (rt)=>{
//     layer_mesh_opacity2.material.uniforms.map.value     = rt.texture
//     layer_mesh_opacity2.material.uniforms.opacity.value = trans_opacity
//     layer_mesh_opacity2.material.uniforms.scale.value   = trans_scale
//     renderer.render(layer_mesh_opacity2, orto_camera)
// }

export const setBokeh = (active,aperture,maxblur)=>{
    bokeh_active   = active
    bokeh_aperture = aperture/10000.0
    bokeh_maxblur  = maxblur
}

const renderBokeh = ()=>{
    let y = 50 - camera.position.y
    let d = Math.sqrt(camera.position.x*camera.position.x+y*y+camera.position.z*camera.position.z)
    Bokeh.material.uniforms.focus.value    = d
    Bokeh.material.uniforms.aspect.value   = aspect
    Bokeh.material.uniforms.aperture.value = bokeh_aperture
    Bokeh.material.uniforms.maxblur.value  = bokeh_maxblur
    renderer.render( Bokeh, orto_camera )
}

export const setLut = (active,t,intensity)=>{
    lut_active = active

    let m = lutPass.material
    if (t.isData3DTexture){
        m.defines.USE_3DTEXTURE = 1
        m.uniforms.lut3d.value = t
    }else{
        m.defines.USE_3DTEXTURE = 0
        m.uniforms.lut.value = t
    }
	//m.needsUpdate = true
    m.uniforms.lutSize.value = t.image.width
    m.uniforms.intensity.value = intensity
}

export const addPass = (pass)=>{
    renderPasses.push(pass)
}

export function animationMixerUpdate (delta) {
    if (mixers.length && settingsData.activeViewMode === ViewMode.Orbit) {
        for (let i=0;i<mixers.length;i++){
            mixers[i].update(delta)
        }
    }
}


export class AutoExposure {
    constructor(renderer, scene, camera) {
        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        this.histogramRenderTarget = new WebGLRenderTarget(256, 256, {
            minFilter: LinearFilter,
            magFilter: LinearFilter,
            format: RGBAFormat
        });
        this.pixelBuffer = new Uint8Array(256 * 256 * 4);
        this.histogram = new Array(16).fill(0);
        this.timeDown = 4;
        this.timeUp = 4;
        this.prevHighExposure = null;
        this.prevLowExposure = null;
    }
    
    calculateSceneHistogram() {
        this.renderer.setRenderTarget(this.histogramRenderTarget);
        this.renderer.render(this.scene, this.camera);

        this.renderer.readRenderTargetPixels(this.histogramRenderTarget, 0, 0, 256, 256, this.pixelBuffer);
        this.renderer.setRenderTarget(null);

        this.histogram.fill(0);
        
        for (let i = 0; i < this.pixelBuffer.length; i += 4) {
            const luminosity =
                this.pixelBuffer[i] / 255 * 0.2125 +
                this.pixelBuffer[i + 1] / 255 * 0.7154 +
                this.pixelBuffer[i + 2] / 255 * 0.0721;
            const bin = Math.floor(luminosity * 16);
            this.histogram[bin]++;
        }
    }

    calculateSceneExposure() {
        this.calculateSceneHistogram();

        const totalPixels = 256 * 256;
        let cumulative = 0;
        for (let i = 0; i < this.histogram.length; i++) {
            cumulative += this.histogram[i];
            if (cumulative >= totalPixels / 2) {
                const targetLuminance = 0.5;
                const currentLuminance = (i + 0.5) / 16;
                console.log('currentLuminance: ', currentLuminance);
                return targetLuminance / currentLuminance;
            }
        }

        return 1.0;
    }

    tick(delta) {
        delta = delta | 1 / 60;
        const newExposure = this.calculateSceneExposure();
        this.renderer.toneMappingExposure = MathUtils.lerp(this.renderer.toneMappingExposure, newExposure, 0.15);
        return;

        if (newExposure < this.renderer.toneMappingExposure) {
            if (this.prevHighExposure === null)
                this.prevHighExposure = this.renderer.toneMappingExposure;
            const diff = this.prevHighExposure - newExposure;
            const velocity = diff / this.timeDown;
            const deltaExposure = velocity * delta;
            // console.log('deltaExp (down):  ', deltaExposure);
            this.renderer.toneMappingExposure -= deltaExposure;
            if (this.renderer.toneMappingExposure < newExposure)
                this.renderer.toneMappingExposure = newExposure;
        }else {
            this.prevHighExposure = null;
        }

        if (newExposure > this.renderer.toneMappingExposure) {
            if (this.prevLowExposure === null)
                this.prevLowExposure = this.renderer.toneMappingExposure;
            const diff = newExposure - this.prevLowExposure;
            const velocity = diff / this.timeUp;
            const deltaExposure = velocity * delta;
            // console.log('deltaExp (up):  ', deltaExposure);
            this.renderer.toneMappingExposure += deltaExposure;
            if (this.renderer.toneMappingExposure > newExposure)
                this.renderer.toneMappingExposure = newExposure;
        }else {
            this.prevLowExposure = null
        }
        // console.log(this.prevHighExposure, this.prevLowExposure, this.renderer.toneMappingExposure);
    }
}
